/**
 * V169.1 → V82.7 Phase 3.2: 승인자 결정 SSoT (BE callable 경유)
 *
 * 기존 동작/타입은 그대로 유지. Firestore direct read 모두 제거.
 * BE 의 `resolveApproverForRequest` / `getMyApproverOrgUnits` callable 사용.
 *
 * BE 의 단일 SSoT 모듈 `functions/src/utils/resolveApprover.ts` 와 동일한 로직이
 * createLeaveRequest / createOvertimeRequest / 본 callable 모두에서 사용됨 (3중 통합).
 *
 * 호환을 위해 기존 시그니처 유지:
 *   - resolveApproverForOrgUnit(db, orgUnitId)  → db 무시 (남기기만 함)
 *   - isApproverForOrgUnit(db, orgUnitId, currentUserId) → db 무시
 *   - getOrgUnitsWhereUserIsApprover(db, currentUserId) → db 와 currentUserId 무시 (BE 가 본인 기준)
 */

import { httpsCallable } from 'firebase/functions';
import { Firestore } from 'firebase/firestore';
import { getFunctions } from '../../firebase/utils';
import { OrgUnit } from '../../types/orgUnit';

/**
 * 승인자 결정 결과
 */
export interface ApproverResolutionResult {
  approverId: string;
  approverName: string;
  resolvedBy: 'approverEmployeeId' | 'role_manager' | 'role_exec';
  candidateCount?: number;
}

export const NO_APPROVER_ERROR_CODE = 'no-approver';

/**
 * 승인자 없음 에러 (BE HttpsError 와 매핑)
 */
export class NoApproverError extends Error {
  code: string = NO_APPROVER_ERROR_CODE;
  orgUnitId: string;
  orgUnitName?: string;

  constructor(orgUnitId: string, orgUnitName?: string) {
    const orgInfo = orgUnitName ? `(${orgUnitName})` : `(orgUnitId: ${orgUnitId})`;
    super(`승인자를 찾을 수 없습니다. 해당 조직${orgInfo}에 승인자가 설정되지 않았습니다. 조직관리에서 승인자(지정 승인자 / 조직장 / 임원)를 설정한 뒤 다시 시도해주세요.`);
    this.name = 'NoApproverError';
    this.orgUnitId = orgUnitId;
    this.orgUnitName = orgUnitName;
  }
}

/**
 * V82.7: BE callable 호출.
 *   - HttpsError(failed-precondition, code='no-approver') → NoApproverError 로 매핑
 *   - 기타 에러는 그대로 throw
 */
export async function resolveApproverForOrgUnit(
  _db: Firestore | null | undefined,
  orgUnitId: string
): Promise<ApproverResolutionResult> {
  try {
    const fn = httpsCallable<
      { orgUnitId: string },
      { success: boolean; approver: ApproverResolutionResult }
    >(getFunctions(), 'resolveApproverForRequest');
    const r = await fn({ orgUnitId });
    return r.data.approver;
  } catch (err: any) {
    // Firebase Functions HttpsError details 안의 code 확인
    const details = err?.details as any;
    if (details?.code === NO_APPROVER_ERROR_CODE) {
      throw new NoApproverError(details.orgUnitId || orgUnitId, details.orgUnitName);
    }
    if (err?.code === 'functions/failed-precondition' && typeof err?.message === 'string' && err.message.includes('승인자를 찾을 수 없습니다')) {
      throw new NoApproverError(orgUnitId);
    }
    throw err;
  }
}

/**
 * V82.7: 본인이 승인자인지 확인
 *   - BE callable 호출 후 approverId === currentUserId 비교
 *   - NoApproverError 는 false 처리 (기존 동작 유지)
 */
export async function isApproverForOrgUnit(
  _db: Firestore | null | undefined,
  orgUnitId: string,
  currentUserId: string
): Promise<boolean> {
  try {
    const result = await resolveApproverForOrgUnit(undefined, orgUnitId);
    return result.approverId === currentUserId;
  } catch (err) {
    if (err instanceof NoApproverError) return false;
    throw err;
  }
}

/**
 * V82.7: 본인이 승인자인 조직 목록
 *   - currentUserId 인자는 호환을 위해 남겼으나, BE 는 request.auth.uid 만 사용
 */
export async function getOrgUnitsWhereUserIsApprover(
  _db: Firestore | null | undefined,
  _currentUserId: string
): Promise<OrgUnit[]> {
  const fn = httpsCallable<{}, { success: boolean; orgUnits: OrgUnit[] }>(
    getFunctions(),
    'getMyApproverOrgUnits'
  );
  const r = await fn({});
  return (r.data.orgUnits || []) as OrgUnit[];
}
